import { supabase } from '../../supabase';
// Importar apenas os tipos que serão realmente retornados/usados
import { Game as IndexGame, RecommendationFilters, UserGamesCriteria } from '../../../types/index'; // Corrigir caminho e importar RecommendationFilters daqui
// Remover LibGame daqui se não for usado diretamente

// Remover interfaces locais temporárias
// interface UserGameWithGame ...
// interface UserGameWithGameAndComplexity ...

// Importar a configuração para exclusões globais e thresholds
import { RECOMMENDATION_CONFIG } from '..';

// Helper para formatar array para filtro 'ov' do Supabase
export function formatArrayForSupabase(arr: string[]): string {
  // Escapa chaves e vírgulas dentro dos itens, se necessário, e envolve em chaves
  const escapedArr = arr.map(item => 
    `"${item.replace(/"/g, '""').replace(/\\/g, '\\\\')}"`
  );
  return `{${escapedArr.join(',')}}`;
}

/**
 * Métodos utilitários para consultas seguras ao banco de dados
 * Encapsula as consultas e trata erros comuns no PostgreSQL/Supabase
 */
export class DatabaseUtils {
  /**
   * Executa uma consulta segura que evita problemas com o operador 'not.in'
   */
  static async executeSafeQuery(
    additionalFilters: Record<string, any> = {},
    limit: number = 10,
    orderByField: string = 'created_at',
    ascending: boolean = false,
    includeOnlyGameIds?: string[]
  ): Promise<IndexGame[]> {
    try {
      // Criar consulta base
      let query = supabase
        .from('games')
        .select('*'); // Limite será aplicado depois dos filtros IN/NOT IN para maior precisão
        
      // Adicionar ordenação (APÓS filtros principais para otimizar)
      // query = query.order(orderByField, { ascending });
      
      // NOVO: Aplicar filtro para incluir apenas IDs específicos, se fornecido
      if (includeOnlyGameIds && includeOnlyGameIds.length > 0) {
        const validIncludeIds = includeOnlyGameIds.filter(id => typeof id === 'string' && id.length > 0);
        if (validIncludeIds.length > 0) {
          console.log(`[DB Utils] Aplicando filtro IN para ${validIncludeIds.length} IDs (includeOnly)`);
          query = query.in('id', validIncludeIds);
        } else {
          // Se includeOnlyGameIds foi fornecido mas vazio/inválido, não deve retornar nada
          console.warn('[DB Utils] includeOnlyGameIds fornecido mas estava vazio ou continha IDs inválidos. Retornando array vazio.');
          return []; 
        }
      }

      // Separar excludeIds dos outros filtros
      let excludeIds: string[] | undefined;
      const filtersToMap: Record<string, any> = {};
      Object.entries(additionalFilters).forEach(([key, value]) => {
        if (key === 'excludeIds' && Array.isArray(value) && value.length > 0) {
          excludeIds = value;
        } else {
          filtersToMap[key] = value;
        }
      });

      // Mapear filtros especiais para os nomes corretos de coluna no banco
      const mappedFilters: Record<string, any> = {};
      
      // Processar filtros (exceto excludeIds)
      Object.entries(filtersToMap).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        
        // Mapear filtros de complexidade para a coluna 'complexity'
        if (key === 'minComplexity') {
          mappedFilters['complexity'] = { gte: value };
        }
        else if (key === 'maxComplexity') {
          mappedFilters['complexity'] = { ...(mappedFilters['complexity'] || {}), lte: value };
        }
        // Mapear filtros de jogadores para min_players/max_players
        else if (key === 'minPlayers') {
          mappedFilters['min_players'] = { gte: value };
        }
        else if (key === 'maxPlayers') {
          mappedFilters['max_players'] = { lte: value };
        }
        // Mapear filtro de tempo de jogo
        else if (key === 'maxPlayTime') {
          mappedFilters['play_time'] = { lte: value };
        }
        // Para ratings
        else if (key === 'gte_average_rating') {
          mappedFilters['average_rating'] = { gte: value };
        }
        // Categorias - usar overlaps
        else if (key === 'categories' && Array.isArray(value)) {
          mappedFilters['categories'] = value;
        }
        // Outros filtros passam direto
        else if (!key.includes('min') && !key.includes('max')) {
          mappedFilters[key] = value;
        }
      });
      
      // Aplicar filtros mapeados (exceto excludeIds)
      Object.entries(mappedFilters).forEach(([field, condition]) => {
        if (condition === undefined || condition === null) return;
        
        if (typeof condition === 'object' && !Array.isArray(condition)) {
          // Aplicar operadores complexos como gte, lte
          Object.entries(condition).forEach(([operator, opValue]) => {
            switch (operator) {
              case 'gte':
                query = query.gte(field, opValue);
                break;
              case 'lte':
                query = query.lte(field, opValue);
                break;
              case 'gt':
                query = query.gt(field, opValue);
                break;
              case 'lt':
                query = query.lt(field, opValue);
                break;
              default:
                query = query.eq(field, opValue);
            }
          });
        } else if (Array.isArray(condition)) {
          // Para arrays, usar overlaps (categorias, mecânicas, etc)
          query = query.overlaps(field, condition);
        } else {
          // Para valores simples, usar equals
          query = query.eq(field, condition);
        }
      });
      
      // === INÍCIO: Aplicar Exclusões Globais ===
      const globalExclusions = RECOMMENDATION_CONFIG.globalExclusions;

      if (globalExclusions.categories && globalExclusions.categories.length > 0) {
        const formattedCategories = formatArrayForSupabase(globalExclusions.categories);
        console.log(`[DB Utils] Aplicando exclusão global de categorias: ${formattedCategories}`);
        query = query.not('categories', 'ov', formattedCategories);
      }

      if (globalExclusions.mechanics && globalExclusions.mechanics.length > 0) {
        const formattedMechanics = formatArrayForSupabase(globalExclusions.mechanics);
        console.log(`[DB Utils] Aplicando exclusão global de mecânicas: ${formattedMechanics}`);
        query = query.not('mechanics', 'ov', formattedMechanics);
      }

      if (globalExclusions.themes && globalExclusions.themes.length > 0) {
        const formattedThemes = formatArrayForSupabase(globalExclusions.themes);
        console.log(`[DB Utils] Aplicando exclusão global de temas: ${formattedThemes}`);
        query = query.not('themes', 'ov', formattedThemes);
      }
      // === FIM: Aplicar Exclusões Globais ===
      
      // Aplicar filtro de exclusão de IDs, se houver (APÓS includeOnlyGameIds)
      if (excludeIds && excludeIds.length > 0) {
        // Garantir que não passamos um array vazio que causaria erro
        const validExcludeIds = excludeIds.filter(id => typeof id === 'string' && id.length > 0);
        if (validExcludeIds.length > 0) {
           console.log(`[DB Utils] Aplicando filtro NOT IN para ${validExcludeIds.length} IDs`);
           query = query.not('id', 'in', `(${validExcludeIds.join(',')})`);
        } else {
          console.warn('[DB Utils] excludeIds continha apenas IDs inválidos ou vazios.');
        }
      }
      
      // Aplicar ordenação ANTES do limit
      query = query.order(orderByField, { ascending });
      // Aplicar limit NO FINAL
      query = query.limit(limit);
      
      // Executar a consulta
      console.log(`Executando query com filtros:`, mappedFilters, `e exclusão de IDs:`, excludeIds ? excludeIds.length : 0);
      const { data, error } = await query;
      
      if (error) {
        console.error('Erro em consulta ao banco de dados:', error);
        return [];
      }
      
      return (data as IndexGame[]) || [];
    } catch (error) {
      console.error('Exceção em consulta ao banco de dados:', error);
      return [];
    }
  }

  private static async _fetchUserGamesInternal(
    userId: string,
    criteria?: UserGamesCriteria
  ): Promise<IndexGame[]> {
    try {
      let query = supabase
        .from('user_games')
        .select('games!user_games_game_id_fkey(*)') // Mantém select com FK
        .eq('user_id', userId);

      if (criteria?.minRating !== undefined) {
        query = query.gte('rating', criteria.minRating);
      }
      
      // Exemplo para futura expansão do critério 'owned' em UserGamesCriteria
      // if (criteria?.owned !== undefined) {
      //   query = query.eq('owned', criteria.owned);
      // }

      if (criteria?.orderBy) {
        query = query.order(criteria.orderBy.field, { ascending: criteria.orderBy.ascending });
      }

      const { data, error } = await query;

      if (error || !data || data.length === 0) {
        if (error) console.error('Erro ao buscar jogos do usuário (interno):', error);
        return [];
      }

      // Mapeamento e filtragem manual e segura
      const userGames: IndexGame[] = [];
      for (const item of data) {
        // Verificar se item é objeto e se item.games é um objeto válido (não null, não array)
        if (item && typeof item === 'object' && item.games && typeof item.games === 'object' && !Array.isArray(item.games)) {
          // Assumir que item.games é do tipo IndexGame
          userGames.push(item.games as IndexGame);
        }
      }
      return userGames;

    } catch (error) {
      console.error('Exceção ao buscar jogos do usuário (interno):', error);
      return [];
    }
  }

  /**
   * Busca jogos favoritos de um usuário
   * Retorna o tipo IndexGame[] pois vem direto do DB
   */
  static async getUserFavoriteGames(userId: string): Promise<IndexGame[]> { 
    // Usar o valor de minRating da configuração centralizada
    const minRatingFromConfig = RECOMMENDATION_CONFIG.recommenderThresholds.minRatingForUserFavorite || 3.0;
    return this._fetchUserGamesInternal(userId, { 
      minRating: minRatingFromConfig, 
      orderBy: { field: 'rating', ascending: false } 
    });
  }

  /**
   * Busca jogos da coleção de um usuário
   * Retorna o tipo IndexGame[] pois vem direto do DB
   */
  static async getUserCollectionGames(userId: string): Promise<IndexGame[]> { 
    return this._fetchUserGamesInternal(userId); // Sem critérios específicos além do userId
  }

  /**
   * Obtém jogos semelhantes com base em categorias
   */
  static async getGamesBySimilarCategories(
    categories: string[],
    excludeIds: string[],
    filters: RecommendationFilters = {},
    limit: number = 10
  ): Promise<IndexGame[]> {
    if (!categories || categories.length === 0) {
      console.warn('Nenhuma categoria fornecida para busca de jogos similares');
      return [];
    }
    
    try {
      console.log(`Buscando jogos com categorias: ${categories.join(', ')}`);
      
      // Tenta com pelo menos 1 categoria em comum (mais tolerante)
      // Preparar filtros para a consulta
      const queryFilters: Record<string, any> = {
        'categories': categories,
        'gte_average_rating': 5.0 // Reduzido para encontrar mais jogos
      };
      
      // Adicionar filtros opcionais - serão processados pelo executeSafeQuery
      if (filters) {
        Object.assign(queryFilters, filters);
      }
      
      // Filtrar jogos excluídos
      if (excludeIds && excludeIds.length > 0) {
        queryFilters['excludeIds'] = excludeIds;
      }
      
      // Executar consulta segura
      let games = await this.executeSafeQuery(
        queryFilters,
        limit * 3, // Aumentar o limite para filtrar manualmente depois
        'average_rating', // Ordenar por avaliação
        false // descendente
      );
      
      // Se não encontrar resultados suficientes, tenta uma busca mais ampla
      if (games.length < limit) {
        console.log('Não encontrou jogos suficientes, tentando busca mais ampla');
        
        // Remover o filtro de rating para ter mais resultados
        delete queryFilters['gte_average_rating'];
        
        // Fazer nova consulta com menos restrições
        const moreGames = await this.executeSafeQuery(
          queryFilters,
          limit * 2,
          'created_at', // Ordenar por data de criação para encontrar jogos mais novos
          false // descendente
        );
        
        // Adicionar jogos encontrados sem duplicação
        const existingIds = new Set(games.map(g => g.id));
        moreGames.forEach(game => {
          if (!existingIds.has(game.id)) {
            games.push(game);
          }
        });
      }
      
      return games.slice(0, limit);
    } catch (error) {
      console.error('Erro ao buscar jogos por categorias similares:', error);
      return [];
    }
  }

  /**
   * Obtém jogos populares
   */
  static async getPopularGames(
    excludeIds: string[] = []
  ): Promise<IndexGame[]> { 
    console.log(`[DB Utils] Buscando jogos populares. Excluir IDs: ${excludeIds.length}. Usando initialLimit da config.`);
    try {
      // additionalFilters para executeSafeQuery pode conter excludeIds
      const filtersForPopular: Record<string, any> = {
        gte_average_rating: RECOMMENDATION_CONFIG.popular.query.minAverageRating 
      };

      // Adicionar excludeIds ao objeto de filtros se houver algum
      if (excludeIds && excludeIds.length > 0) {
        filtersForPopular.excludeIds = excludeIds;
      }
      
      // Usar executeSafeQuery com o initialLimit da config e ordenação apropriada.
      // O DataService fará o corte final para o count desejado.
      return await this.executeSafeQuery(
        filtersForPopular, // Agora contém excludeIds, se aplicável
        RECOMMENDATION_CONFIG.popular.query.initialLimit, // Usar o initialLimit da config para a query
        'average_rating', // Ordenar por avaliação média
        false // Descendente
      );
    } catch (error) {
      console.error('Erro ao buscar jogos populares:', error);
      return [];
    }
  }

  /**
   * Obtém jogos recentes
   */
  static async getRecentGames(
    excludeIds: string[] = [],
    limit: number = 10
  ): Promise<IndexGame[]> { 
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*') // Retorna IndexGame[]
        .order('created_at', { ascending: false })
        .limit(limit * 2);
        
      if (error) {
        console.error('Erro ao buscar jogos recentes:', error);
        return [];
      }
      
      const excludeSet = new Set(excludeIds);
       // Cast seguro para IndexGame[]
      const allGames = (data as IndexGame[]) || []; 
      const filteredGames = allGames.filter(game => !excludeSet.has(game.id));
      
      return filteredGames.slice(0, limit);
    } catch (error) {
      console.error('Exceção ao buscar jogos recentes:', error);
      return [];
    }
  }
} 